import { env } from '@/shared/config';
import {
  HttpError,
  LOGIN_REQUIRED_EVENT,
  MAINTENANCE_EVENT,
  NetworkError,
  TimeoutError,
  isLoginRequiredError,
  isMaintenanceError,
} from './errors';
import { applyMockEmpty } from './mockEmpty';
import { MOCK_ERROR_DELAY_MS, shouldMockError } from './mockErrors';

/** Значение заголовка `Authorization` целиком: `tma <initData>` или `Bearer <token>`. */
type AuthorizationProvider = () => string | undefined;

let getAuthorization: AuthorizationProvider = () => undefined;

/**
 * Транспорт не знает ни про Telegram, ни про веб-сессию: провайдер заголовка
 * подставляет `app/bootstrap.ts` — initData в мини-аппе, токен сессии в
 * браузере (см. `shared/lib/webSession`).
 */
export function setAuthorizationProvider(provider: AuthorizationProvider): void {
  getAuthorization = provider;
}

/**
 * Заголовок авторизации отдельно от `apiRequest` — для запросов, которые не
 * укладываются в «JSON туда, JSON обратно»: загрузка аудиофайла и получение
 * звука в ответ.
 */
export function authHeaders(): Record<string, string> {
  const authorization = getAuthorization();
  return authorization ? { Authorization: authorization } : {};
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Сигнал вызывающего (отмена при размонтировании). */
  signal?: AbortSignal;
  timeoutMs?: number;
  /**
   * Запрос переживёт закрытие страницы — для последнего пульса времени
   * практики при сворачивании мини-аппа (см. `usePracticeTimer`).
   */
  keepalive?: boolean;
  /**
   * Фоновый запрос, сбой которого человек не видит (обновление профиля при
   * возврате в приложение): сбой сети и таймаут не идут в служебный чат.
   * Safari на iPhone после сворачивания будит сеть не сразу, и такой запрос
   * падал «нет сети» на каждом возврате (2026-10-28). Ответ сервера с ошибкой
   * (401 и т. п.) по-прежнему идёт обработчикам.
   */
  quiet?: boolean;
}

const DEFAULT_TIMEOUT_MS = 15_000;

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, timeoutMs = DEFAULT_TIMEOUT_MS, keepalive, quiet } = options;
  const url = `${env.backendUrl}${path}`;

  if (env.isDev && shouldMockError(path, method)) {
    await new Promise((resolve) => setTimeout(resolve, MOCK_ERROR_DELAY_MS));
    throw new NetworkError(url, new TypeError('Load failed (mockErrors)'));
  }

  // AbortSignal.any() ещё нет в части WebView (iOS < 17.4), поэтому объединяем
  // внешний сигнал и таймаут вручную.
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  const abortFromCaller = () => controller.abort();
  signal?.addEventListener('abort', abortFromCaller, { once: true });

  const headers: Record<string, string> = authHeaders();
  // Явный Content-Type на любом методе с телом — даже пустом ({}), а не
  // только когда вызывающий сам что-то передал. Без своего заголовка тело
  // отсутствует вовсе, и некоторые прокси (бесплатный pinggy-тоннель, где
  // тестируется Mini App) в этом случае подставляют свой Content-Type
  // (application/octet-stream), который Fastify не умеет парсить и отвечает
  // 415 — сброс онбординга и завершение диалога молча не доезжали именно
  // из-за этого.
  const hasBody = method !== 'GET';
  if (hasBody) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      signal: controller.signal,
      keepalive,
      body: hasBody ? JSON.stringify(body ?? {}) : undefined,
    });
  } catch (error) {
    if (timedOut) throw new TimeoutError(url, quiet);
    // Отмена вызывающим — не ошибка приложения, пробрасываем как есть.
    if (signal?.aborted) throw error;
    throw new NetworkError(url, error, quiet);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abortFromCaller);
  }

  if (!response.ok) {
    const error = new HttpError(response.status, url, await response.text().catch(() => ''));
    // Технические работы включили, пока человек внутри: экран меняет `main.tsx`,
    // а не каждый вызывающий со своим «Повторить».
    if (isMaintenanceError(error)) window.dispatchEvent(new Event(MAINTENANCE_EVENT));
    // Гость веб-версии дошёл до того, что открыто только после входа, — на
    // экран входа ведёт `AppRouter`, а не каждый вызывающий.
    if (isLoginRequiredError(error)) window.dispatchEvent(new Event(LOGIN_REQUIRED_EVENT));
    throw error;
  }
  if (response.status === 204) return undefined as T;
  const data = (await response.json()) as T;
  return env.isDev ? applyMockEmpty(path, method, data) : data;
}
